import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '__components/Kizen/Input';
import { Button } from '@kizen/kds/Button';
import {
  Row,
  Card,
  Fields,
  FormWrapper,
  Header,
  InputWrapper,
  Footer,
  SuccessMessage,
  VideoWrapper,
  CTAWrapper,
  Testimonial,
} from './styles';
import { Typography } from '@kizen/kds/Typography';
import { DUPLICATE_RESPONSE_CODE } from '../constants';
import { Spacer } from '@kizen/kds/Spacer';
import OutlookSVG from 'graphics/outlook.svg?react';
import GmailSVG from 'graphics/gmail.svg?react';
import { BusinessBuilderMailServices, KZN_FRAME_EVENTS } from '../events';
import useFormValidation from '__hooks/useFormValidation';
import TextInput from '__components/Inputs/TextInput';
import { getDataQAForInput } from '__components/Inputs/helpers';
import EmailAddressTextInput from '__components/Inputs/TextInput/presets/EmailAddress';
import { toastVariant, useToast } from '__components/ToastProvider';

interface CompleteSessionForm {
  onConfirm: (params: {
    first_name: string;
    last_name: string;
    email: string;
    company_name: string;
    job_title: string;
    num_employees: number;
  }) => any;
  loading: boolean;
  error: string;
  clearError: () => void;
  isCompleted: boolean;
  padding?: string;
  trackEvent?: (event: string, payload?: any) => void;
}

export const CompleteSessionForm = ({
  onConfirm,
  loading,
  clearError,
  isCompleted,
  padding,
  trackEvent,
}: CompleteSessionForm) => {
  const { t } = useTranslation();
  const [showToast] = useToast();

  const [values, setValues] = useState({
    firstName: '',
    lastName: '',
    email: '',
    companyName: '',
    jobTitle: '',
    numberOfEmployees: '',
  });

  const {
    firstName,
    lastName,
    email,
    companyName,
    jobTitle,
    numberOfEmployees,
  } = values;

  const {
    validateFormState,
    validationProps,
    handleInputChange,
    setValidationState,
    firstFieldWithErrorRef,
  } = useFormValidation({
    formState: values,
    setFormState: setValues,
  });

  const getEmailClickHandler = useCallback(
    (service: BusinessBuilderMailServices) => {
      return () => {
        trackEvent?.(KZN_FRAME_EVENTS.OPEN_EMAIL, service);
      };
    },
    [trackEvent]
  );

  const handleSubmit = useCallback(async () => {
    if (!validateFormState(t)) {
      return;
    }
    const error = await onConfirm({
      first_name: firstName,
      last_name: lastName,
      email,
      company_name: companyName,
      job_title: jobTitle,
      num_employees: parseInt(numberOfEmployees),
    });

    if (error === DUPLICATE_RESPONSE_CODE) {
      firstFieldWithErrorRef.current = 'companyName';
      setValidationState({
        ['companyName']: t('Business name already exists'),
      });
    } else if (error) {
      showToast({
        variant: toastVariant.FAILURE,
        message: error,
      });
    }
  }, [
    t,
    showToast,
    firstFieldWithErrorRef,
    validateFormState,
    setValidationState,
    firstName,
    lastName,
    email,
    companyName,
    jobTitle,
    numberOfEmployees,
    onConfirm,
  ]);

  return (
    <Card padding={padding}>
      {isCompleted ? (
        <SuccessMessage>
          <Typography size="3xl">
            {t(
              'We’ve just created your Kizen Account and our AI is building your application!'
            )}
          </Typography>
          <Spacer size={30} />
          <Typography size="xl">
            {t(
              'Check your email for your personal link to set up your password and sign in to the extraordinary!'
            )}
          </Typography>
          <Spacer size={30} />
          <CTAWrapper>
            <a
              href="https://mail.google.com"
              target="_blank"
              rel="noreferrer"
              onClick={getEmailClickHandler('gmail')}
            >
              <GmailSVG />
            </a>
            <a
              href="https://outlook.office.com"
              target="_blank"
              rel="noreferrer"
              onClick={getEmailClickHandler('outlook')}
            >
              <OutlookSVG />
            </a>
          </CTAWrapper>
          <Spacer size={30} />
          <VideoWrapper>
            <div className="w-full animate-fadeIn">
              <video loop muted autoPlay playsInline className="w-full h-full">
                <source
                  src="https://kizen.com/media/kizen-email-verification.mp4"
                  type="video/mp4"
                />
              </video>
            </div>
          </VideoWrapper>
        </SuccessMessage>
      ) : (
        <>
          <Header>
            <Typography size="3xl">
              {t("You're moments away from accessing the extraordinary")}
            </Typography>
            <Spacer size={30} />
            <Typography size="xl">
              {t(
                'Enter your information below to set up your account, no credit card required'
              )}
            </Typography>
          </Header>
          <FormWrapper padding={padding}>
            <Fields>
              <Row>
                <InputWrapper>
                  <TextInput
                    type={Input.INPUT_TYPES.TEXT}
                    label={t('First Name')}
                    value={firstName}
                    onChange={(value: string, ev: any, errors: any) =>
                      handleInputChange('firstName', value, errors)
                    }
                    placeholder={t('First Name')}
                    {...validationProps('firstName')}
                    {...getDataQAForInput('first-name')}
                    required
                  />
                </InputWrapper>
                <InputWrapper>
                  <TextInput
                    type={Input.INPUT_TYPES.TEXT}
                    label={t('Last Name')}
                    value={lastName}
                    onChange={(value: string, ev: any, errors: any) =>
                      handleInputChange('lastName', value, errors)
                    }
                    placeholder={t('Last Name')}
                    {...validationProps('lastName')}
                    {...getDataQAForInput('last-name')}
                    required
                  />
                </InputWrapper>
              </Row>
              <Row>
                <InputWrapper>
                  <EmailAddressTextInput
                    label={t('Email')}
                    value={email}
                    onChange={(value: string, ev: any, errors: any) =>
                      handleInputChange('email', value, errors)
                    }
                    placeholder={t('Email')}
                    {...validationProps('email')}
                    {...getDataQAForInput('email')}
                    type={Input.INPUT_TYPES.EMAIL}
                    required
                  />
                </InputWrapper>
                <InputWrapper>
                  <TextInput
                    type={Input.INPUT_TYPES.TEXT}
                    label={t('Job Title')}
                    value={jobTitle}
                    onChange={(value: string, ev: any, errors: any) =>
                      handleInputChange('jobTitle', value, errors)
                    }
                    placeholder={t('Job Title')}
                    {...validationProps('jobTitle')}
                    {...getDataQAForInput('job-title')}
                    required
                  />
                </InputWrapper>
              </Row>
              <Row>
                <InputWrapper>
                  <TextInput
                    label={t('Company Name')}
                    value={companyName}
                    onChange={(value: string, ev: any, errors: any) => {
                      clearError();
                      handleInputChange('companyName', value, errors);
                    }}
                    placeholder={t('Company Name')}
                    {...validationProps('companyName')}
                    {...getDataQAForInput('company-name')}
                    required
                  />
                </InputWrapper>
                <InputWrapper>
                  <TextInput
                    type={Input.INPUT_TYPES.NUMBER}
                    label={t('How many employees work at your company?')}
                    value={numberOfEmployees}
                    onChange={(value: string, ev: any, errors: any) =>
                      handleInputChange('numberOfEmployees', value, errors)
                    }
                    placeholder={t('Employee Count')}
                    {...validationProps('numberOfEmployees')}
                    {...getDataQAForInput('number-of-employees')}
                    required
                  />
                </InputWrapper>
              </Row>
            </Fields>

            <Footer>
              <Button onClick={handleSubmit} disabled={loading}>
                {t('Submit')}
              </Button>
            </Footer>
            <Testimonial>
              <div>
                {/* Intentionally not translated */}
                <Typography size="lg">
                  “In my 30 years of IT and Operations leadership I have never
                  seen a platform that can deliver as fast as KIZEN. It’s very
                  impressive. With Kizen, we’ve been able to make even our most
                  difficult to please team members very happy.”
                </Typography>
                <Spacer size={30} />
                <Typography>-COO of Insurance Brokerage</Typography>
              </div>
            </Testimonial>
          </FormWrapper>
        </>
      )}
    </Card>
  );
};
